import { Trans, useLingui } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import { UserFromGetMany } from '@/models/user';

export const USERS_PER_PAGE = 10;

interface UsersTableProps {
  users: UserFromGetMany[];
  isLoading: boolean;
  isError: boolean;
  page: number;
  onPageChange: (page: number) => void;
  count: number | undefined;
  entity?: { id: string };
}

export const UsersTable = ({
  users,
  isLoading,
  isError,
  page,
  onPageChange,
  count,
  entity,
}: UsersTableProps) => {
  const { t } = useLingui();

  return (
    <TableQueryWrapper isLoading={isLoading} isError={isError}>
      {users.length === 0 ? (
        <EmptyState
          subtitle={
            <Stack>
              <Paragraph>
                <Trans id="settings.organization.members.users.no-results.1">
                  No results are matching your criteria.
                </Trans>
              </Paragraph>
              <Paragraph>
                <Trans id="settings.organization.members.users.no-results.2">
                  Please try with different keywords or filters.
                </Trans>
              </Paragraph>
            </Stack>
          }
          illustration={<IllustrationNoResults />}
        />
      ) : (
        <Table
          ariaLabel={t({ id: 'settings.entity.members.table.label', message: 'Members' })}
          columnWidths={['1fr', '1fr']}
          bordered
        >
          <Header>
            <Row>
              <Column>
                <Trans id="settings.entity.members.table.header.name">Name</Trans>
              </Column>
              <Column>
                <Trans id="settings.entity.members.table.header.role">Roles</Trans>
              </Column>
            </Row>
          </Header>
          <Body>
            {users.map((user) => (
              <UserRow key={user.id} user={user} entity={entity} />
            ))}
          </Body>
          <PaginationFooter
            page={page}
            itemsPerPage={USERS_PER_PAGE}
            count={count}
            onPageChange={onPageChange}
          />
        </Table>
      )}
    </TableQueryWrapper>
  );
};

const UserRow = ({ user, entity }: { user: UserFromGetMany; entity?: { id: string } }) => {
  const roles = user.roles.filter(
    (userRole) => !entity || !userRole.entity || userRole.entity.id === entity.id
  );

  const rolesGrouped = Object.values(
    roles.reduce(
      (acc, userRole) => {
        if (!acc[userRole.role.id]) {
          acc[userRole.role.id] = {
            role: userRole.role,
            entities: [],
          };
        }

        if (userRole.entity) {
          acc[userRole.role.id].entities.push(userRole.entity);
        }

        return acc;
      },
      {} as Record<
        string,
        {
          role: {
            id: string;
            name: string;
          };
          entities: {
            id: string;
            name: string;
          }[];
        }
      >
    )
  );

  return (
    <Row key={user.id} to={`/settings_beta/team/users/${user.id}`}>
      <Cell isLink className="sentry-mask">
        <Stack gap="0">
          <Paragraph size="100">{user.name}</Paragraph>
          <Paragraph size="200" color="secondary">
            {user.email}
          </Paragraph>
        </Stack>
      </Cell>
      <Cell>
        <Stack gap="0.25rem">
          {entity &&
            roles.map((userRole) => (
              <Paragraph key={userRole.role.id} size="100">
                {userRole.role.name}
              </Paragraph>
            ))}

          {!entity &&
            rolesGrouped.map(({ role, entities }) => (
              <Paragraph key={role.id} size="100">
                {entities.length > 0
                  ? `${role.name} (${entities.map((entity) => entity.name).join(', ')})`
                  : role.name}
              </Paragraph>
            ))}
        </Stack>
      </Cell>
    </Row>
  );
};
